<template>
  <div class="app flex">
    <!-- Sidebar -->
    <aside class="sidebar">
      <ul>
        <li
            :class="{ active: selectedMenu === 'login' }"
            @click="selectMenu('login')"
        >
          login
        </li>
        <li
            v-for="table in Object.keys(apiPaths)"
            :key="table"
            :class="{ active: selectedMenu === table }"
            @click="selectMenu(table)"
        >
          {{ table }}
        </li>
      </ul>
    </aside>

    <!-- Main Content -->
    <main class="main-content">
      <h1>{{ selectedMenu }}</h1>

      <!-- Login Page -->
      <div v-if="selectedMenu === 'login'" class="token-management">
        <label for="bearer-token">Bearer Token</label>
        <textarea
            id="bearer-token"
            v-model="bearerToken"
            placeholder="Enter your Bearer token here..."
        ></textarea>
        <div class="button-group">
          <button @click="saveToken">Save Token</button>
          <button @click="clearToken" class="danger">Clear Token</button>
        </div>
      </div>

      <!-- Table Data -->
      <div v-else-if="selectedMenu">
        <div v-if="loading" class="loading">Loading...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <table v-else>
          <thead>
          <tr>
            <th v-for="(value, key) in sampleRow" :key="key">{{ key }}</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="record in records" :key="record.id">
            <td v-for="(value, key) in record" :key="key">{{ value }}</td>
            <td>
              <button @click="deleteRecord(record.id)">Delete</button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </main>
  </div>
</template>

<script setup>
import {ref} from "vue";
import axios from "axios";

// Base URL for API
// const baseUrl = "https://gwapi.servicelazio.it/pral2023/integration"
const baseUrl = process.env.NODE_ENV === 'development' 
  ? "/api/v1"
  : "https://api.pral5.starfarm.it/api/v1"
// const baseUrl = "http://localhost:3000"

// API Paths Dictionary
const apiPaths = {
  "users": "admin/users",
  "assistiti": "admin/assistiti",
  "scheda-contatto": "scheda-contatto",
  "scheda-consulenza": "scheda-consulenza",
  "prima-accoglienza": "prima-accoglienza",
  "seconda-accoglienza": "seconda-accoglienza",
  "semi-autonomia": "semi-autonomia",
  "tirocinio": "tirocinio",
  "presa-in-carico-territoriale": "presa-in-carico-territoriale",
};

// State variables
const selectedMenu = ref("login");
const records = ref([]);
const loading = ref(false);
const error = ref(null);
const sampleRow = ref({});
const bearerToken = ref(localStorage.getItem("bearerToken") || "");

// Fetch records for the selected table
const fetchRecords = async () => {
  if (!bearerToken.value) {
    error.value = "Bearer token is not set.";
    return;
  }

  const apiPath = apiPaths[selectedMenu.value];
  if (!apiPath) {
    error.value = "Invalid API path.";
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    const response = await axios.get(`${baseUrl}/${apiPath}`, {
      headers: { Authorization: `Bearer ${bearerToken.value}` },
    });

    if (selectedMenu.value === "users") {
      let utenti = response.data.utenti || [];
      records.value = utenti.map((user) => ({
        id: user.cf,
        details: user.details || {}, // Ensure details object exists
        given_name: user.details?.given_name || '',
        family_name: user.details?.family_name || '',
        email: user.details?.email || '',
        last_login: user.last_login || null,
        role: user.role || null
      }));
    } else {
      let items = response.data.items || [];
      if (selectedMenu.value === "assistiti") {
        items = response.data.assistiti;
      }
      records.value = items.map(item => ({
        ...item,
        id: item._id?.$oid,
      }));
      console.log(response.data);
    }

    sampleRow.value = records.value.length > 0 ? records.value[0] : {};
  } catch (err) {
    error.value = err.response?.data?.message || "Error fetching records.";
  } finally {
    loading.value = false;
  }
};

// Delete a record by ID
const deleteRecord = async (id) => {
  if (!bearerToken.value) {
    alert("Bearer token is not set.");
    return;
  }

  const apiPath = apiPaths[selectedMenu.value];
  if (!apiPath) {
    alert("Invalid API path.");
    return;
  }

  const criticalAccounts = ["LVLDAA85T50G702B", "SRRFRZ68H04H501O", "SPZMRC69E17H501N", "MNZCLD69M19H501R"];
  if (criticalAccounts.includes(id)) {
    alert("Could not delete " + id);
    return;
  }

  const confirmed = window.confirm("Are you sure you want to delete record " + id);
  if (!confirmed) {
    return; // Exit if the user cancels
  }

  try {
    await axios.delete(`${baseUrl}/${apiPath}/${id}`, {
      headers: { Authorization: `Bearer ${bearerToken.value}` },
    });
    records.value = records.value.filter((record) => record.id !== id);
  } catch (err) {
    alert(err.response?.data?.message || "Error deleting record.");
  }
};

// Manage Bearer token
const saveToken = () => {
  localStorage.setItem("bearerToken", bearerToken.value);
  alert("Bearer token saved.");
};

const clearToken = () => {
  bearerToken.value = "";
  localStorage.removeItem("bearerToken");
  alert("Bearer token cleared.");
};

// Handle menu selection
const selectMenu = (menu) => {
  selectedMenu.value = menu;

  // Fetch records only for tables, not the login menu
  if (menu !== "login") {
    fetchRecords();
  }
};
</script>

<style>
/* Styles remain unchanged */
.app {
  display: flex;
  height: 100vh;
}

.sidebar {
  width: 200px;
  background: #f4f4f4;
  padding: 1rem;
}

.sidebar ul {
  list-style: none;
  padding: 0;
}

.sidebar li {
  padding: 0.5rem;
  cursor: pointer;
}

.sidebar li.active {
  background: #ddd;
  font-weight: bold;
}

.main-content {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
}

.token-management {
  margin-bottom: 2rem;
}

.token-management label {
  display: block;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.token-management textarea {
  width: 100%;
  height: 80px;
  margin-bottom: 1rem;
  padding: 0.5rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.button-group {
  display: flex;
  gap: 1rem;
}

button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

button:hover {
  opacity: 0.9;
}

button.danger {
  background: #e74c3c;
  color: white;
}

button:not(.danger) {
  background: #3498db;
  color: white;
}

.loading,
.error {
  margin-top: 1rem;
  color: #e74c3c;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

table th,
table td {
  border: 1px solid #ddd;
  padding: 0.5rem;
}
</style>
